import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, NavBar, Picker, Toast } from 'antd-mobile';
import type { PickerColumn } from 'antd-mobile/es/components/picker';
import { DownOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { useDeliveryTaskOrder } from '../store/deliveryStore';
import { useMutation, useQuery, ApolloError } from '@apollo/client';
import { CREATE_ADDRESS_COORDINATE_FEEDBACK, CreateAddressCoordinateFeedbackInput } from '../graphql/mutations/createAddressCoordinateFeedback';
import {
    GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
    GetAddressLocationTypeDictConfigResponse,
    AddressLocationTypeDictItem,
} from '../../../graphql/queries/getAddressLocationTypeDictConfig';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import AddressCoordinateFeedbackMapView from './components/AddressCoordinateFeedbackMapView';

const AddressCoordinateFeedbackPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams<{ trackingNumber?: string }>();
    const { t, i18n } = useTranslation();
    const deliveryTaskOrderId = location.state?.deliveryTaskOrderId;
    const { deliveryTaskOrder } = useDeliveryTaskOrder(deliveryTaskOrderId);
    // 优先从URL参数获取运单号，如果没有则从location.state获取，最后从deliveryTaskOrder获取
    const trackingNumber = params.trackingNumber ?? location.state?.trackingNumber ?? deliveryTaskOrder?.trackingNumber;
    const [selectedLatitude, setSelectedLatitude] = useState<number | null>(null);
    const [selectedLongitude, setSelectedLongitude] = useState<number | null>(null);
    const [driverLatitude, setDriverLatitude] = useState<number | null>(null);
    const [driverLongitude, setDriverLongitude] = useState<number | null>(null);
    const [createFeedback, { loading: isSubmitting }] = useMutation(CREATE_ADDRESS_COORDINATE_FEEDBACK);
    // 使用 useRef 保存当前提交的 requestId，确保弱网重复调用时使用相同的 requestId
    const currentRequestIdRef = useRef<string | null>(null);

    const { data: typeDictData } = useQuery<GetAddressLocationTypeDictConfigResponse>(
        GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
        { fetchPolicy: 'cache-first' }
    );
    const typeDictItems: AddressLocationTypeDictItem[] = useMemo(
        () => (typeDictData?.getAddressLocationTypeDictConfig ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
        [typeDictData]
    );
    const [selectedSecondaryCode, setSelectedSecondaryCode] = useState<string | null>(null);
    const [typePickerVisible, setTypePickerVisible] = useState(false);

    const currentLang = (i18n.language || 'zh').toLowerCase();
    const pickDropdownName = (item: AddressLocationTypeDictItem) => {
        if (currentLang.startsWith('en')) return item.dropdownNameEn;
        if (currentLang.startsWith('es')) return item.dropdownNameEs;
        return item.dropdownNameZh;
    };

    // 二级 code 全局唯一，单列 Picker 展示下拉文案
    const typePickerColumns: PickerColumn[] = useMemo(() => {
        return [
            typeDictItems.map((item) => ({
                label: pickDropdownName(item),
                value: item.secondaryCode,
            })),
        ];
    }, [typeDictItems, currentLang]);

    const selectedTypeItem = useMemo(
        () => typeDictItems.find((it) => it.secondaryCode === selectedSecondaryCode) ?? null,
        [typeDictItems, selectedSecondaryCode]
    );
    const selectedTypeLabel = selectedTypeItem ? pickDropdownName(selectedTypeItem) : '';

    // 获取司机当前GPS定位
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setDriverLatitude(position.coords.latitude);
                    setDriverLongitude(position.coords.longitude);
                },
                (error) => {
                    console.warn('获取司机定位失败:', error);
                }
            );
        }
    }, []);

    // 优先使用从上一个页面传递过来的坐标，如果没有则使用从 store 获取的数据
    const deliveryAddress = deliveryTaskOrder?.orderWithAddress?.deliveryAddress;
    const orderAddressId = deliveryAddress?.id;
    // 确保坐标是数字类型
    const originalLatitude = location.state?.originalLatitude != null 
        ? Number(location.state.originalLatitude) 
        : (deliveryAddress?.latitude != null ? Number(deliveryAddress.latitude) : null);
    const originalLongitude = location.state?.originalLongitude != null 
        ? Number(location.state.originalLongitude) 
        : (deliveryAddress?.longitude != null ? Number(deliveryAddress.longitude) : null);
    const address = location.state?.address ?? (deliveryAddress ? 
        `${deliveryAddress.addressLine1 || ''} ${deliveryAddress.addressLine2 || ''} ${deliveryAddress.city || ''} ${deliveryAddress.state || ''} ${deliveryAddress.zipcode || ''}`.trim() : '');
    // 获取缩放层级，如果没有则使用默认值15
    const zoom = location.state?.zoom != null ? Number(location.state.zoom) : 15;
    // 获取stopNumber，如果没有则尝试从deliveryTaskOrder获取
    const stopNumber = location.state?.stopNumber ?? (deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : null);

    // 调试日志：检查坐标传递
    useEffect(() => {
        console.log('AddressCoordinateFeedbackPage - location.state:', location.state);
        console.log('AddressCoordinateFeedbackPage - originalLatitude from state:', location.state?.originalLatitude);
        console.log('AddressCoordinateFeedbackPage - originalLongitude from state:', location.state?.originalLongitude);
        console.log('AddressCoordinateFeedbackPage - final originalLatitude:', originalLatitude);
        console.log('AddressCoordinateFeedbackPage - final originalLongitude:', originalLongitude);
        console.log('AddressCoordinateFeedbackPage - deliveryAddress from store:', deliveryAddress);
    }, [location.state, originalLatitude, originalLongitude, deliveryAddress]);

    // 初始化选中坐标为原始坐标（立即初始化，不等待 deliveryTaskOrder 加载）
    useEffect(() => {
        if (originalLatitude != null && originalLongitude != null) {
            // 只有当 selectedLatitude 和 selectedLongitude 还没有被设置时才初始化
            if (selectedLatitude === null && selectedLongitude === null) {
                setSelectedLatitude(originalLatitude);
                setSelectedLongitude(originalLongitude);
            }
        }
    }, [originalLatitude, originalLongitude]);


    const handleBack = () => {
        navigate(-1);
    };

    const handleCoordinateChange = (lat: number, lng: number) => {
        setSelectedLatitude(lat);
        setSelectedLongitude(lng);
    };

    const handleSubmit = async () => {
        // 如果 orderAddressId 不可用，尝试从 deliveryTaskOrder 获取
        const finalOrderAddressId = orderAddressId ?? deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id;
        if (!finalOrderAddressId) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.orderAddressIncomplete'), position: 'center' });
            return;
        }

        if (!selectedLatitude || !selectedLongitude) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.pleaseSelectPosition'), position: 'center' });
            return;
        }

        if (!selectedTypeItem) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.pleaseSelectAddressType'), position: 'center' });
            return;
        }

        // 检查位置是否更新（距离必须在10米到20公里之间）
        if (originalLatitude && originalLongitude) {
            const distance = calculateDistance(
                originalLatitude,
                originalLongitude,
                selectedLatitude,
                selectedLongitude
            );
            if (distance < 10) {
                Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.distanceTooShort'), position: 'center' });
                return;
            }
            if (distance > 20000) {
                Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.distanceTooLong'), position: 'center' });
                return;
            }
        }

        // 防止重复提交：如果正在提交中，直接返回
        if (isSubmitting) {
            return;
        }

        try {
            // 生成或复用请求唯一ID，确保弱网重复调用时使用相同的 requestId
            // 如果还没有生成 requestId，则生成一个新的；如果已有，则复用（防止重复提交）
            if (!currentRequestIdRef.current) {
                currentRequestIdRef.current = `feedback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            }
            const requestId = currentRequestIdRef.current;

            const input: CreateAddressCoordinateFeedbackInput = {
                orderAddressId: finalOrderAddressId,
                originalLatitude: originalLatitude ?? null,
                originalLongitude: originalLongitude ?? null,
                feedbackLatitude: selectedLatitude,
                feedbackLongitude: selectedLongitude,
                requestId: requestId,
                trackingNumber: trackingNumber ?? null,
                driverLatitude: driverLatitude,
                driverLongitude: driverLongitude,
                primaryTypeCode: selectedTypeItem.primaryCode,
                secondaryTypeCode: selectedTypeItem.secondaryCode
            };

            await createFeedback({
                variables: { input }
            });

            // 提交成功后，清除 requestId，允许下次提交
            currentRequestIdRef.current = null;
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.thankYouFeedback'), position: 'center' });
            navigate(-1);
        } catch (error) {
            // 提交失败后，清除 requestId，允许重试
            currentRequestIdRef.current = null;
            console.error('提交反馈失败:', error);
            
            // 如果是 GraphQL 错误，说明已经被全局 errorLink 处理了，不需要再显示通用错误消息
            if (error instanceof ApolloError && error.graphQLErrors && error.graphQLErrors.length > 0) {
                // GraphQL 错误已经被全局 errorLink 处理，不再显示通用错误消息
                return;
            }
            
            // 非 GraphQL 错误（如网络错误等），显示通用错误消息
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.submitFailed'), position: 'center' });
        }
    };

    // 计算两点间距离（米）
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 6371000; // 地球半径（米）
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // 如果坐标信息不可用，显示加载状态
    // 但如果从 location.state 传递了坐标，即使 deliveryTaskOrder 还没加载，也可以显示地图
    const hasCoordinates = originalLatitude != null && originalLongitude != null;
    const isLoading = !hasCoordinates && (!deliveryTaskOrder || !deliveryAddress);
    const displayLatitude = selectedLatitude ?? originalLatitude;
    const displayLongitude = selectedLongitude ?? originalLongitude;
    const coordinateText = (displayLatitude != null && displayLongitude != null)
        ? `${displayLatitude.toFixed(7)}, ${displayLongitude.toFixed(7)}`
        : t('pages.delivery.addressCoordinateFeedback.notSelected');

    if (isLoading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={handleBack}>{t('pages.delivery.addressCoordinateFeedback.title')}</NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <p>{t('pages.delivery.addressCoordinateFeedback.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            {/* Navbar */}
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={handleBack}>{t('pages.delivery.addressCoordinateFeedback.title')}</NavBar>
            </div>

            {/* Map */}
            {originalLatitude != null && originalLongitude != null && (
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 relative">
                        <AddressCoordinateFeedbackMapView
                            latitude={selectedLatitude ?? originalLatitude}
                            longitude={selectedLongitude ?? originalLongitude}
                            originalLatitude={originalLatitude}
                            originalLongitude={originalLongitude}
                            onCoordinateChange={handleCoordinateChange}
                            showCenterIndicator={true}
                            enableDragToSelect={true}
                            zoom={zoom}
                            stopNumber={stopNumber}
                        />
                    </div>
                </div>
            )}

            {/* Form Card */}
            <Card className="flex-0 m-2 rounded-lg">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.addressType')}
                            <span style={{ color: '#cf1322', marginLeft: 4 }}>*</span>
                        </p>
                        <div
                            onClick={() => setTypePickerVisible(true)}
                            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-base leading-6 cursor-pointer flex items-center justify-between"
                        >
                            <span className={selectedTypeLabel ? 'text-[#222]' : 'text-[#999]'}>
                                {selectedTypeLabel || t('pages.delivery.addressCoordinateFeedback.selectPlaceholder')}
                            </span>
                            <DownOutline fontSize={18} color="#999" />
                        </div>
                    </div>
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.currentCoordinate')}
                        </p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 leading-6 break-words cursor-text select-text">
                            {coordinateText}
                        </div>
                    </div>
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.address')}
                        </p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 leading-6 break-words cursor-text select-text">
                            {address || '-'}
                        </div>
                    </div>
                </div>
            </Card>

            <Picker
                columns={typePickerColumns}
                visible={typePickerVisible}
                value={selectedSecondaryCode ? [selectedSecondaryCode] : []}
                onClose={() => setTypePickerVisible(false)}
                onConfirm={(value) => {
                    const code = (value?.[0] as string | undefined) ?? null;
                    setSelectedSecondaryCode(code);
                }}
                cancelText={t('pages.delivery.addressCoordinateFeedback.cancel')}
                confirmText={t('pages.delivery.addressCoordinateFeedback.confirm')}
            />

            {/* Bottom Buttons */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    color="default"
                    onClick={handleBack}
                    block
                    style={{
                        '--background-color': 'gray',
                        '--text-color': 'white'
                    }}
                >
                    {t('pages.delivery.addressCoordinateFeedback.cancel')}
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    block
                >
                    {t('pages.delivery.addressCoordinateFeedback.confirm')}
                </Button>
            </div>
        </div>
    );
};

export default AddressCoordinateFeedbackPage;

